import _extends from "@babel/runtime/helpers/esm/extends";
import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["accordion", "children", "className", "contentTag", "embedded", "fwdRef", "headerTag", "indented", "onClose", "onOpen", "size"];
import React, { createContext, forwardRef, useMemo, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { buildVersionedClassNames } from '@op/opds.opux-utilities';
export const ExpanderListContext = /*#__PURE__*/createContext({
  accordion: false,
  contentTag: 'div',
  current: undefined,
  embedded: false,
  headerTag: 'div',
  indented: false,
  onClose: () => undefined,
  onOpen: () => undefined,
  registerItem: () => undefined,
  size: undefined,
  toggleExpander: () => undefined
});
const useExpanderListContext = _ref => {
  let {
    accordion,
    contentTag,
    current,
    embedded,
    headerTag,
    indented,
    onClose: _onClose,
    onOpen: _onOpen,
    registerItem: _registerItem,
    size,
    toggleExpander: _toggleExpander
  } = _ref;
  return useMemo(() => ({
    accordion,
    contentTag,
    current,
    embedded,
    headerTag,
    indented,
    onClose: expanderId => {
      if (_onClose) {
        _onClose(expanderId);
      }
    },
    onOpen: expanderId => {
      if (_onOpen) {
        _onOpen(expanderId);
      }
    },
    registerItem: () => _registerItem ? _registerItem() : undefined,
    size,
    toggleExpander: expanderId => _toggleExpander == null ? void 0 : _toggleExpander(expanderId)
  }), [accordion, contentTag, current, embedded, headerTag, indented, _onClose, _onOpen, _registerItem, size, _toggleExpander]);
};

/**
 * _A list of expanders._
 *
 * ***
 *
 * Wrapper element for creating a list of `<Expander>`. Allows to
 * pass down the Expander props to all children and additional props (including `ref`) will be
 * forwarded to the wrapping `<div>` element.
 *
 * See the [Expander](#/Show%20%26%20Hide/Expander) guidelines for an example.
 */
const ExpanderList = _ref2 => {
  let {
      accordion = false,
      children,
      className,
      contentTag,
      embedded,
      fwdRef,
      headerTag,
      indented,
      onClose,
      onOpen,
      size
    } = _ref2,
    props = _objectWithoutPropertiesLoose(_ref2, _excluded);
  const [open, setOpen] = useState(undefined);
  function registerItem() {
    return "expander_" + uuidv4();
  }
  const expanderListClassName = buildVersionedClassNames('ds-expander-list', className);
  const contextValue = useExpanderListContext({
    accordion,
    contentTag,
    current: open,
    embedded,
    headerTag,
    indented,
    onClose,
    onOpen,
    registerItem,
    size,
    toggleExpander: setOpen
  });
  return /*#__PURE__*/React.createElement("div", _extends({}, props, {
    className: expanderListClassName,
    ref: fwdRef
  }), /*#__PURE__*/React.createElement(ExpanderListContext.Provider, {
    value: contextValue
  }, children));
};
const Fwd = /*#__PURE__*/forwardRef((props, ref) => /*#__PURE__*/React.createElement(ExpanderList, _extends({}, props, {
  fwdRef: ref
})));
Fwd.displayName = 'ExpanderList';
export default Fwd;